// src/main/SecurityDepositTracking.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Avatar,
    Tabs,
    Tab,
    Alert,
    Snackbar,
} from '@mui/material';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent,
    TimelineOppositeContent,
} from '@mui/lab';

import {
    Delete,
    Search,
    Add as AddIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print,
    History,
    AttachMoney,
    CurrencyExchange,
    PriceChange,
    Gavel,
    AccountBalance as AccountBalanceIcon,
    Savings as SavingsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIPropertyList,
    APIApartmentList,
    APIFloorUnitList,
    APITenantList,
    APISecurityDepositList,
    APISecurityDepositStore,
    APISecurityDepositUpdate,
    APISecurityDepositDelete,
    APISecurityDepositExportExcel,
    APISecurityDepositExportPDF,
    APISecurityDepositPrint,
    APISecurityDepositStats,
    APISecurityDepositDeduct,
    APISecurityDepositRefund,
    APISecurityDepositForfeit,
    APISecurityDepositHistory
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SecurityDepositTracking = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [statistics, setStatistics] = useState(null);
    const [properties, setProperties] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [apartments, setApartments] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [deductDialogOpen, setDeductDialogOpen] = useState(false);
    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [forfeitDialogOpen, setForfeitDialogOpen] = useState(false);
    const [deductAmount, setDeductAmount] = useState('');
    const [deductReason, setDeductReason] = useState('');
    const [refundAmount, setRefundAmount] = useState('');
    const [refundNotes, setRefundNotes] = useState('');
    const [forfeitReason, setForfeitReason] = useState('');
    const [depositHistory, setDepositHistory] = useState([]);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

    const isMounted = useRef(true);

    const depositStatusOptions = [
        { id: 'held', name: 'Held', color: 'info', icon: <AccountBalanceIcon />, description: 'Deposit is being held' },
        { id: 'partially_deducted', name: 'Partially Deducted', color: 'warning', icon: <PriceChange />, description: 'Some amount has been deducted' },
        { id: 'refunded', name: 'Refunded', color: 'success', icon: <CurrencyExchange />, description: 'Deposit has been refunded' },
        { id: 'forfeited', name: 'Forfeited', color: 'error', icon: <Gavel />, description: 'Deposit has been forfeited' }
    ];

    const [formData, setFormData] = useState({
        tenant_id: '',
        apartment_id: '',
        property_id: '',
        floor_number: '',
        apartment_number: '',
        deposit_amount: '',
        date_received: new Date().toISOString().split('T')[0],
        deduction_amount: 0,
        deduction_reason: '',
        refund_amount: 0,
        refund_date: '',
        deposit_status: 'held',
        notes: '',
        receipt_number: '',
        payment_method: 'cash'
    });

    const [filters, setFilters] = useState({
        deposit_status: '',
        property_id: '',
        tenant_id: '',
        date_from: '',
        date_to: '',
        min_amount: '',
        max_amount: ''
    });

    const [errors, setErrors] = useState({
        tenant_id: false,
        apartment_id: false,
        deposit_amount: false,
        date_received: false
    });

    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchProperties();
        fetchTenants();
        fetchDeposits();
        fetchStatistics();

        return () => {
            isMounted.current = false;
        };
    }, [showToast]);

    const fetchProperties = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIPropertyList);
            if (result.success && isMounted.current) {
                setProperties(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        }
    };

    const fetchTenants = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APITenantList);
            if (result.success && isMounted.current) {
                setTenants(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching tenants:", error);
            if (isMounted.current) setTenants([]);
        }
    };

    const fetchApartmentsForTenant = async (tenantId) => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIFloorUnitList, { tenant_id: tenantId });
            if (result.success && isMounted.current) {
                setApartments(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching apartments:", error);
            if (isMounted.current) setApartments([]);
        }
    };

    const fetchDeposits = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APISecurityDepositList);

            if (result.success && isMounted.current) {
                const deposits = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: deposits
                }));
            } else if (isMounted.current) {
                setError(result.message);
            }
        } catch (error) {
            if (isMounted.current) {
                setError("Failed to load data");
            }
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APISecurityDepositStats);
            if (result.success && result.data && isMounted.current) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Update fetchDepositHistory to explicitly include user_id and branch_id in query parameters
    const fetchDepositHistory = async (depositId) => {
        try {
            const userBranchId = getUserDetails("branchId");
            const userId = getUserDetails("Id");
            
            const result = await apiService.listWithParams(userBranchId, APISecurityDepositHistory, { 
                deposit_id: depositId,
                branch_id: userBranchId,
                user_id: userId
            });
            
            if (result.success && result.data && isMounted.current) {
                setDepositHistory(result.data);
            }
        } catch (error) {
            console.error("Error fetching history:", error);
            if (isMounted.current) setDepositHistory([]);
        }
    };

    // Update handleOpenView to reset activeTab and load history immediately
    const handleCloseView = () => setOpenView(false);
    const handleOpenView = async (record) => {
        setSelectedRecord(record);
        setActiveTab(0); // Reset to the first tab (Deposit Info)
        setOpenView(true);
        await fetchDepositHistory(record.id); // Pre-fetch history for the tab layout
    };

    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };
    
    const handlePrintDeposit = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print deposit details", "error");
            return;
        }
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));

            const result = await apiService.print(APISecurityDepositPrint, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file generated successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to generate PDF file", "error");
            }
        }
    };
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export deposit data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToExcel(APISecurityDepositExportExcel, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export Excel file", "error");
            }
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export deposit data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APISecurityDepositExportPDF, formDataToSend);

            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export PDF file", "error");
            }
        }
    };

    const handleDeduct = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to deduct from deposits", "error");
            return;
        }

        if (!deductAmount || parseFloat(deductAmount) <= 0) {
            showToast("Validation Error", "Please enter a valid deduction amount", "warning");
            return;
        }

        if (!deductReason.trim()) {
            showToast("Validation Error", "Please provide a reason for deduction", "warning");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('deposit_id', selectedRecord.id);
            formDataToSend.append('deduction_amount', deductAmount);
            formDataToSend.append('deduction_reason', deductReason);

            const result = await apiService.create(APISecurityDepositDeduct, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setDeductDialogOpen(false);
                setDeductAmount('');
                setDeductReason('');
                fetchDeposits();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Deduct error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to deduct from deposit", "error");
            }
        }
    };

    const handleRefund = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to refund deposits", "error");
            return;
        }

        if (!refundAmount || parseFloat(refundAmount) <= 0) {
            showToast("Validation Error", "Please enter a valid refund amount", "warning");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('deposit_id', selectedRecord.id);
            formDataToSend.append('refund_amount', refundAmount);
            formDataToSend.append('refund_notes', refundNotes);

            const result = await apiService.create(APISecurityDepositRefund, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setRefundDialogOpen(false);
                setRefundAmount('');
                setRefundNotes('');
                fetchDeposits();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Refund error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to refund deposit", "error");
            }
        }
    };

    const handleForfeit = async () => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to forfeit deposits", "error");
            return;
        }

        if (!forfeitReason.trim()) {
            showToast("Validation Error", "Please provide a reason for forfeiture", "warning");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('deposit_id', selectedRecord.id);
            formDataToSend.append('forfeit_reason', forfeitReason);

            const result = await apiService.create(APISecurityDepositForfeit, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setForfeitDialogOpen(false);
                setForfeitReason('');
                fetchDeposits();
                fetchStatistics();
            }
        } catch (error) {
            console.error("Forfeit error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to forfeit deposit", "error");
            }
        }
    };

    const handleViewHistory = async (record) => {
        setSelectedRecord(record);
        await fetchDepositHistory(record.id);
        setHistoryDialogOpen(true);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            deposit_status: '',
            property_id: '',
            tenant_id: '',
            date_from: '',
            date_to: '',
            min_amount: '',
            max_amount: ''
        });
        setSearchTerm('');
        setSelectedProperty('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);
    // const handleCloseView = () => setOpenView(false);
    // const handleOpenView = (record) => {
    //     setSelectedRecord(record);
    //     setOpenView(true);
    // };

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add security deposits", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            tenant_id: '',
            apartment_id: '',
            property_id: '',
            floor_number: '',
            apartment_number: '',
            deposit_amount: '',
            date_received: new Date().toISOString().split('T')[0],
            deduction_amount: 0,
            deduction_reason: '',
            refund_amount: 0,
            refund_date: '',
            deposit_status: 'held',
            notes: '',
            receipt_number: '',
            payment_method: 'cash'
        });
        setErrors({
            tenant_id: false,
            apartment_id: false,
            deposit_amount: false,
            date_received: false
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit security deposits", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            tenant_id: record.tenant_id || '',
            apartment_id: record.apartment_id || '',
            property_id: record.property_id || '',
            floor_number: record.floor_number || '',
            apartment_number: record.apartment_number || '',
            deposit_amount: record.deposit_amount || '',
            date_received: record.date_received || '',
            deduction_amount: record.deduction_amount || 0,
            deduction_reason: record.deduction_reason || '',
            refund_amount: record.refund_amount || 0,
            refund_date: record.refund_date || '',
            deposit_status: record.deposit_status || 'held',
            notes: record.notes || '',
            receipt_number: record.receipt_number || '',
            payment_method: record.payment_method || 'cash'
        });
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete security deposits", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleOpenDeduct = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to deduct from deposits", "error");
            return;
        }
        setSelectedRecord(record);
        setDeductAmount('');
        setDeductReason('');
        setDeductDialogOpen(true);
    };

    const handleOpenRefund = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to refund deposits", "error");
            return;
        }
        setSelectedRecord(record);
        setRefundAmount(record.deposit_amount - (record.deduction_amount || 0));
        setRefundNotes('');
        setRefundDialogOpen(true);
    };

    const handleOpenForfeit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to forfeit deposits", "error");
            return;
        }
        setSelectedRecord(record);
        setForfeitReason('');
        setForfeitDialogOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        if (name === 'tenant_id') {
            fetchApartmentsForTenant(value);
            setFormData(prev => ({
                ...prev,
                [name]: value,
                apartment_id: ''
            }));
            return;
        }


        if (name === 'apartment_id') {
            // Safely find the apartment by converting both IDs to strings, and checking for null/undefined values
            const selectedApt = apartments.find(a => 
                a && 
                a.id !== undefined && 
                a.id !== null && 
                String(a.id) === String(value)
            );
            
            if (selectedApt) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    property_id: selectedApt.property_id || '',
                    floor_number: selectedApt.unit_name || 'N/A',
                    apartment_number: selectedApt.floor_name || 'N/A',
                    deposit_amount: selectedApt.security_deposit || prev.deposit_amount
                }));
            } else {
                // Reset related fields if no apartment is found or if the selection is cleared
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    property_id: '',
                    floor_number: '',
                    apartment_number: '',
                    deposit_amount: ''
                }));
            }
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateForm = () => {
        if (!formData.tenant_id) {
            showToast("Validation Error", "Please select a tenant", "warning");
            return false;
        }
        if (!formData.apartment_id) {
            showToast("Validation Error", "Please select an apartment", "warning");
            return false;
        }
        if (!formData.deposit_amount || parseFloat(formData.deposit_amount) <= 0) {
            showToast("Validation Error", "Deposit amount is required and must be greater than 0", "warning");
            return false;
        }
        if (!formData.date_received) {
            showToast("Validation Error", "Date received is required", "warning");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add security deposits", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update security deposits", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete security deposits", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        if (postStatus !== "DELETE") {
            formDataToSend.append('tenant_id', formData.tenant_id);
            formDataToSend.append('apartment_id', formData.apartment_id);
            formDataToSend.append('deposit_amount', formData.deposit_amount);
            formDataToSend.append('floor_number', formData.floor_number || '0');
            formDataToSend.append('apartment_number', formData.apartment_number || 'N/A');
            formDataToSend.append('date_received', formData.date_received);
            formDataToSend.append('deposit_status', formData.deposit_status);
            formDataToSend.append('notes', formData.notes || '');
            formDataToSend.append('receipt_number', formData.receipt_number || '');
            formDataToSend.append('payment_method', formData.payment_method);
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APISecurityDepositStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APISecurityDepositUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APISecurityDepositDelete, formDataToSend);
            }

            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchDeposits();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
            if (isMounted.current) {
                showToast("Error", error.response?.data?.message || "Operation failed", "error");
            }
        }
    };

    const getStatusColor = (status) => {
        const option = depositStatusOptions.find(opt => opt.id === status);
        return option?.color || 'default';
    };

    const getStatusIcon = (status) => {
        const option = depositStatusOptions.find(opt => opt.id === status);
        return option?.icon || <AccountBalanceIcon />;
    };

    const getStatusText = (status) => {
        const option = depositStatusOptions.find(opt => opt.id === status);
        return option ? option.name : status;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getCurrentBalance = (record) => {
        if (!record) return 0;
        return (record.deposit_amount || 0) - (record.deduction_amount || 0) - (record.refund_amount || 0);
    };

    const filteredData = useMemo(() => {
        return pageState.pageData.filter(record => {
            const matchesSearch = searchTerm === '' ||
                record.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.apartment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = !filters.deposit_status || 
                record.deposit_status === filters.deposit_status;

            const matchesProperty = !filters.property_id || 
                record.property_id?.toString() === filters.property_id;

            const matchesTenant = !filters.tenant_id || 
                record.tenant_id?.toString() === filters.tenant_id;

            let matchesDate = true;
            if (filters.date_from && record.date_received) {
                matchesDate = matchesDate && record.date_received >= filters.date_from;
            }
            if (filters.date_to && record.date_received) {
                matchesDate = matchesDate && record.date_received <= filters.date_to;
            }

            const matchesMinAmount = !filters.min_amount ||
                (record.deposit_amount && record.deposit_amount >= parseFloat(filters.min_amount));
            const matchesMaxAmount = !filters.max_amount ||
                (record.deposit_amount && record.deposit_amount <= parseFloat(filters.max_amount));

            return matchesSearch && matchesStatus && matchesProperty && 
                   matchesTenant && matchesDate && matchesMinAmount && matchesMaxAmount;
        });
    }, [pageState.pageData, searchTerm, filters]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return <LoadingState message="Loading Security Deposits..." variant="medical" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchDeposits} title="Unable to Load Security Deposit Data" variant="medical" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Deposits Held</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {formatCurrency(statistics.total_deposits_held || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                    <SavingsIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Deducted</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                        {formatCurrency(statistics.total_deducted || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                    <PriceChange sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Refunded</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                        {formatCurrency(statistics.total_refunded || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                    <CurrencyExchange sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Active Deposits</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                        {statistics.active_deposits || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                    <AccountBalanceIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, flexShrink: 0 }}>
                        Security Deposit Tracking ({recordsCount(filteredData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                New Deposit
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                        )}
                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Export Data
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => { fetchDeposits(); fetchStatistics(); }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search deposits..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': { color: 'white', '&::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
            </Paper>

            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}><TableChart fontSize="small" color="success" /> Export to Excel (.xlsx)</MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}><PictureAsPdf fontSize="small" color="error" /> Export to PDF (.pdf)</MenuItem>
            </Menu>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.primary.main, '& th': { color: 'white', fontWeight: 'bold', fontSize: '0.95rem' } }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Tenant</TableCell>
                                    <TableCell>Property</TableCell>
                                    <TableCell align="right">Deposit Amount</TableCell>
                                    <TableCell align="right">Deducted</TableCell>
                                    <TableCell align="right">Refunded</TableCell>
                                    <TableCell align="right">Current Balance</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Date Received</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => {
                                    const currentBalance = getCurrentBalance(record);
                                    return (
                                        <TableRow key={record.id || index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                            <TableCell><Chip label={page * rowsPerPage + index + 1} color="primary" size="small" /></TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{record.tenant_name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{record.tenant_phone}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{record.property_name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{record.apartment_number} ({record.floor_number})</Typography>
                                            </TableCell>
                                            <TableCell align="right">{formatCurrency(record.deposit_amount)}</TableCell>
                                            <TableCell align="right" sx={{ color: theme.palette.warning.main }}>{formatCurrency(record.deduction_amount || 0)}</TableCell>
                                            <TableCell align="right" sx={{ color: theme.palette.success.main }}>{formatCurrency(record.refund_amount || 0)}</TableCell>
                                            <TableCell align="right">
                                                <Typography sx={{ fontWeight: 'bold', color: currentBalance > 0 ? theme.palette.primary.main : theme.palette.text.secondary }}>
                                                    {formatCurrency(currentBalance)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(record.deposit_status)}
                                                    label={getStatusText(record.deposit_status)}
                                                    color={getStatusColor(record.deposit_status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{record.date_received}</TableCell>
                                            <TableCell align="center">
                                                {hasPermission('VIEW') && (
                                                    <Tooltip title="View Details"><IconButton onClick={() => handleOpenView(record)} color="primary"><ViewIcon /></IconButton></Tooltip>
                                                )}
                                                {hasPermission('PRINT') && (
                                                    <Tooltip title="Print"><IconButton onClick={() => handlePrintDeposit(record)} color="info"><Print /></IconButton></Tooltip>
                                                )}
                                                {hasPermission('EDIT') && (
                                                    <>
                                                        <Tooltip title="Edit"><IconButton onClick={() => handleOpenEdit(record)} color="secondary"><EditIcon /></IconButton></Tooltip>
                                                        {record.deposit_status === 'held' && (
                                                            <>
                                                                <Tooltip title="Deduct"><IconButton onClick={() => handleOpenDeduct(record)} color="warning"><PriceChange /></IconButton></Tooltip>
                                                                <Tooltip title="Refund"><IconButton onClick={() => handleOpenRefund(record)} color="success"><CurrencyExchange /></IconButton></Tooltip>
                                                                <Tooltip title="Forfeit"><IconButton onClick={() => handleOpenForfeit(record)} color="error"><Gavel /></IconButton></Tooltip>
                                                            </>
                                                        )}
                                                        {record.deposit_status === 'partially_deducted' && (
                                                            <>
                                                                <Tooltip title="Deduct More"><IconButton onClick={() => handleOpenDeduct(record)} color="warning"><PriceChange /></IconButton></Tooltip>
                                                                <Tooltip title="Refund Balance"><IconButton onClick={() => handleOpenRefund(record)} color="success"><CurrencyExchange /></IconButton></Tooltip>
                                                            </>
                                                        )}
                                                        <Tooltip title="View History"><IconButton onClick={() => handleViewHistory(record)} color="info"><History /></IconButton></Tooltip>
                                                    </>
                                                )}
                                                {hasPermission('DELETE') && (
                                                    <Tooltip title="Delete"><IconButton onClick={() => handleOpenDelete(record)} color="error"><Delete /></IconButton></Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100]} component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ mt: 2 }} />
                    {filteredData.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                            <SavingsIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No security deposits found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={clearFilters}>Show All Deposits</Button>
                        <Divider>OR</Divider>
                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>Filter Deposits</Typography>
                        <Box>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Deposit Status</InputLabel>
                                    <Select name="deposit_status" value={filters.deposit_status} onChange={handleFilterChange} label="Deposit Status">
                                        <MenuItem value="">All Status</MenuItem>
                                        {depositStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Property</InputLabel>
                                    <Select name="property_id" value={filters.property_id} onChange={handleFilterChange} label="Property">
                                        <MenuItem value="">All Properties</MenuItem>
                                        {properties.map(property => <MenuItem key={property.id} value={property.id}>{property.property_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Tenant</InputLabel>
                                    <Select name="tenant_id" value={filters.tenant_id} onChange={handleFilterChange} label="Tenant">
                                        <MenuItem value="">All Tenants</MenuItem>
                                        {tenants.map(tenant => <MenuItem key={tenant.id} value={tenant.id}>{tenant.full_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date From" name="date_from" type="date" value={filters.date_from} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date To" name="date_to" type="date" value={filters.date_to} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Min Amount" name="min_amount" type="number" value={filters.min_amount} onChange={handleFilterChange} placeholder="Min amount" />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Max Amount" name="max_amount" type="number" value={filters.max_amount} onChange={handleFilterChange} placeholder="Max amount" />
                            </Grid>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>{postStatus === "ADD" ? "New Security Deposit" : "Edit Security Deposit"}</b></DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>Deposit Information</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Select Tenant *</InputLabel>
                                    <Select name="tenant_id" value={formData.tenant_id} onChange={handleInputChange} label="Select Tenant *">
                                        <MenuItem value="">Select a tenant</MenuItem>
                                        {tenants.map(tenant => <MenuItem key={tenant.id} value={tenant.id}>{tenant.full_name} - {tenant.phone_number}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Select Unit *</InputLabel>
                                    <Select name="apartment_id" value={formData.apartment_id} onChange={handleInputChange} label="Select Unit *" disabled={!formData.tenant_id}>
                                        <MenuItem value="">Select a unit</MenuItem>
                                        {apartments.map(apt => (
                                            <MenuItem key={apt.id} value={apt.id}>
                                                {apt.property_name} - {apt.floor_name || apt.floor_number || apt.floor_id || '0'} - Unit {apt.unit_name || apt.apartment_number || apt.unit_number || 'N/A'}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Deposit Amount (UGX) *" name="deposit_amount" type="number" value={formData.deposit_amount} onChange={handleInputChange} required InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment> }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Date Received *" name="date_received" type="date" value={formData.date_received} onChange={handleInputChange} required InputLabelProps={{ shrink: true }} />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Receipt Number" name="receipt_number" value={formData.receipt_number} onChange={handleInputChange} placeholder="Receipt reference" />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Payment Method</InputLabel>
                                    <Select name="payment_method" value={formData.payment_method} onChange={handleInputChange} label="Payment Method">
                                        <MenuItem value="cash">Cash</MenuItem>
                                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                                        <MenuItem value="card">Card</MenuItem>
                                        <MenuItem value="mobile_money">Mobile Money</MenuItem>
                                        <MenuItem value="check">Check</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Deposit Status</InputLabel>
                                    <Select name="deposit_status" value={formData.deposit_status} onChange={handleInputChange} label="Deposit Status">
                                        {depositStatusOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleInputChange} multiline rows={3} placeholder="Additional notes..." />
                            </Grid><br/>
                        </Box>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">Cancel</Button>
                            <Button type="submit" color="primary" variant="contained">{postStatus === "ADD" ? "Create Deposit" : "Update Deposit"}</Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={deductDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setDeductDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Deduct from Security Deposit</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>This will deduct the amount from the tenant's security deposit.</Alert>
                        <Typography variant="body1" gutterBottom>Tenant: <strong>{selectedRecord?.tenant_name}</strong></Typography>
                        <Typography variant="body2" gutterBottom>Current Deposit Balance: <strong>{formatCurrency(getCurrentBalance(selectedRecord))}</strong></Typography>
                        <TextField fullWidth label="Deduction Amount (UGX) *" type="number" value={deductAmount} onChange={(e) => setDeductAmount(e.target.value)} sx={{ mt: 2 }} InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment> }} />
                        <TextField fullWidth label="Reason for Deduction *" value={deductReason} onChange={(e) => setDeductReason(e.target.value)} multiline rows={3} sx={{ mt: 2 }} placeholder="e.g., Damage repair, cleaning fees, etc." />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setDeductDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleDeduct} color="warning" variant="contained">Confirm Deduction</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={refundDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setRefundDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Refund Security Deposit</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>This will refund the specified amount to the tenant.</Alert>
                        <Typography variant="body1" gutterBottom>Tenant: <strong>{selectedRecord?.tenant_name}</strong></Typography>
                        <Typography variant="body2" gutterBottom>Current Deposit Balance: <strong>{formatCurrency(getCurrentBalance(selectedRecord))}</strong></Typography>
                        <TextField fullWidth label="Refund Amount (UGX) *" type="number" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} sx={{ mt: 2 }} InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment> }} />
                        <TextField fullWidth label="Refund Notes" value={refundNotes} onChange={(e) => setRefundNotes(e.target.value)} multiline rows={3} sx={{ mt: 2 }} placeholder="Additional notes about the refund..." />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setRefundDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleRefund} color="success" variant="contained">Confirm Refund</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={forfeitDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setForfeitDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Forfeit Security Deposit</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Alert severity="error" sx={{ mb: 2 }}>This will forfeit the entire remaining deposit balance. This action cannot be undone!</Alert>
                        <Typography variant="body1" gutterBottom>Tenant: <strong>{selectedRecord?.tenant_name}</strong></Typography>
                        <Typography variant="body2" gutterBottom>Amount to be forfeited: <strong>{formatCurrency(getCurrentBalance(selectedRecord))}</strong></Typography>
                        <TextField fullWidth label="Reason for Forfeiture *" value={forfeitReason} onChange={(e) => setForfeitReason(e.target.value)} multiline rows={3} sx={{ mt: 2 }} placeholder="e.g., Breach of contract, unpaid rent, etc." />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={() => setForfeitDialogOpen(false)} variant="contained">Cancel</Button>
                    <Button onClick={handleForfeit} color="error" variant="contained">Confirm Forfeit</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Security Deposit Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Deposit Info" icon={<SavingsIcon />} iconPosition="start" />
                                <Tab label="Transaction History" icon={<History />} iconPosition="start" />
                            </Tabs>

                            {activeTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Box>
                                        <Grid item xs={12}><Typography variant="h6" color="primary">Deposit Information</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Deposit ID:</b> {selectedRecord.deposit_id}</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Status:</b> <Chip icon={getStatusIcon(selectedRecord.deposit_status)} label={getStatusText(selectedRecord.deposit_status)} color={getStatusColor(selectedRecord.deposit_status)} size="small" /></Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Deposit Amount:</b> {formatCurrency(selectedRecord.deposit_amount)}</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Date Received:</b> {selectedRecord.date_received}</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Total Deducted:</b> {formatCurrency(selectedRecord.deduction_amount || 0)}</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Total Refunded:</b> {formatCurrency(selectedRecord.refund_amount || 0)}</Typography></Grid><br/>
                                        <Grid item xs={12}><Typography variant="subtitle2"><b>Current Balance:</b> <strong>{formatCurrency(getCurrentBalance(selectedRecord))}</strong></Typography></Grid><br/>
                                        {selectedRecord.deduction_reason && <Grid item xs={12}><Typography variant="subtitle2"><b>Deduction Reason:</b> {selectedRecord.deduction_reason}</Typography></Grid>}<br/>
                                        {selectedRecord.refund_date && <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Refund Date:</b> {selectedRecord.refund_date}</Typography></Grid>}<br/>
                                        {selectedRecord.receipt_number && <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Receipt Number:</b> {selectedRecord.receipt_number}</Typography></Grid>}<br/>
                                        {selectedRecord.payment_method && <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Payment Method:</b> {selectedRecord.payment_method}</Typography></Grid>}<br/>
                                        {selectedRecord.notes && <Grid item xs={12}><Typography variant="subtitle2"><b>Notes:</b> {selectedRecord.notes}</Typography></Grid>}<br/>
                                        <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>Property Information</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Tenant:</b> {selectedRecord.tenant_name}</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Property:</b> {selectedRecord.property_name}</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Apartment:</b> {selectedRecord.apartment_number} (Floor {selectedRecord.floor_number})</Typography></Grid><br/>
                                        <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ mt: 2 }}>System Information</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Created:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography></Grid><br/>
                                        <Grid item xs={12} sm={6}><Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography></Grid>
                                    </Box>
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    {depositHistory.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No transaction history found</Typography>
                                    ) : (
                                        <Timeline position="alternate">
                                            {depositHistory.map((event, index) => (
                                                <TimelineItem key={index}>
                                                    <TimelineOppositeContent color="text.secondary">{new Date(event.created_at).toLocaleString()}</TimelineOppositeContent>
                                                    <TimelineSeparator>
                                                        <TimelineDot color={event.action_type === 'create' ? 'success' : event.action_type === 'deduct' ? 'warning' : event.action_type === 'refund' ? 'info' : 'error'}>
                                                            {event.action_type === 'create' ? <AddIcon /> : event.action_type === 'deduct' ? <PriceChange /> : event.action_type === 'refund' ? <CurrencyExchange /> : <Gavel />}
                                                        </TimelineDot>
                                                        {index < depositHistory.length - 1 && <TimelineConnector />}
                                                    </TimelineSeparator>
                                                    <TimelineContent>
                                                        <Paper elevation={2} sx={{ p: 2 }}>
                                                            <Typography variant="h6" component="span">{event.title}</Typography>
                                                            <Typography variant="body2" color="text.secondary">{event.description}</Typography>
                                                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>By: {event.performed_by_name || 'System'}</Typography>
                                                        </Paper>
                                                    </TimelineContent>
                                                </TimelineItem>
                                            ))}
                                        </Timeline>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">Close</Button>
                    {hasPermission('PRINT') && selectedRecord && <Button onClick={() => handlePrintDeposit(selectedRecord)} color="info" variant="contained"><Print sx={{ mr: 1 }} /> Print</Button>}
                </DialogActions>
            </Dialog>

            <Dialog
                open={historyDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setHistoryDialogOpen(false);
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Deposit Transaction History</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        {depositHistory.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No transaction history found</Typography>
                        ) : (
                            <Timeline position="alternate">
                                {depositHistory.map((event, index) => (
                                    <TimelineItem key={index}>
                                        <TimelineOppositeContent color="text.secondary">{new Date(event.created_at).toLocaleString()}</TimelineOppositeContent>
                                        <TimelineSeparator>
                                            <TimelineDot color={event.action_type === 'create' ? 'success' : event.action_type === 'deduct' ? 'warning' : event.action_type === 'refund' ? 'info' : 'error'}>
                                                {event.action_type === 'create' ? <AddIcon /> : event.action_type === 'deduct' ? <PriceChange /> : event.action_type === 'refund' ? <CurrencyExchange /> : <Gavel />}
                                            </TimelineDot>
                                            {index < depositHistory.length - 1 && <TimelineConnector />}
                                        </TimelineSeparator>
                                        <TimelineContent>
                                            <Paper elevation={2} sx={{ p: 2 }}>
                                                <Typography variant="h6" component="span">{event.title}</Typography>
                                                <Typography variant="body2" color="text.secondary">{event.description}</Typography>
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>By: {event.performed_by_name || 'System'}</Typography>
                                            </Paper>
                                        </TimelineContent>
                                    </TimelineItem>
                                ))}
                            </Timeline>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions><Button color="error" onClick={() => setHistoryDialogOpen(false)} variant="contained">Close</Button></DialogActions>
            </Dialog>

            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Security Deposit Record</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>Are you sure you want to delete this security deposit record?</Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center' }}>
                            {selectedRecord.tenant_name} - {formatCurrency(selectedRecord.deposit_amount)}
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    icon={getStatusIcon(selectedRecord.deposit_status)}
                                    label={getStatusText(selectedRecord.deposit_status)}
                                    color={getStatusColor(selectedRecord.deposit_status)}
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>Warning: This action cannot be undone!</Typography>
                </DialogContent>
                <DialogActions><Button color="error" onClick={handleCloseDelete} variant="contained">Cancel</Button><Button onClick={handleSubmit} color="primary" variant="contained">Confirm Delete</Button></DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default SecurityDepositTracking;


